import { Bot, Braces, Package, Webhook, Workflow } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CopyButton } from '../components/ui.js';

/**
 * Integrações: um cartão por caminho de entrada na plataforma, com o snippet mínimo e
 * o link para onde a configuração de fato acontece. Nada aqui é uma integração "de
 * catálogo" que não existe — cada cartão aponta para algo que funciona hoje.
 */
export function IntegrationsPage() {
  const origin = window.location.origin;
  const cards: Array<{
    key: string;
    icon: ReactNode;
    title: string;
    tag?: string;
    body: string;
    snippet?: string;
    to: string;
    cta: string;
  }> = [
    {
      key: 'rest',
      icon: <Braces />,
      title: 'API REST',
      body: 'Toda a plataforma por HTTPS com JSON, autenticada por API key. Idempotência nos envios, request id em toda resposta.',
      snippet: `curl '${origin}/v1/instances' \\\n  -H 'Authorization: Bearer tk_live_SUA_CHAVE_AQUI'`,
      to: '/developers/explorer',
      cta: 'Abrir o API Explorer',
    },
    {
      key: 'sdk',
      icon: <Package />,
      title: 'SDK para Node.js e TypeScript',
      body: 'Timeout, retentativa seletiva e idempotência já configurados. Uma chamada por operação do contrato.',
      snippet: `npm install @talkium/sdk\n\nimport { Talkium } from '@talkium/sdk';\nconst talkium = new Talkium({ apiKey: process.env.TALKIUM_API_KEY! });\nawait talkium.messages.sendText({ instanceId, to: '5511999990000', text: 'Olá!' });`,
      to: '/developers/keys',
      cta: 'Criar uma API key',
    },
    {
      key: 'mcp',
      icon: <Bot />,
      title: 'Agentes de IA (MCP)',
      tag: 'Novo',
      body: 'Claude Code, Cursor e qualquer cliente MCP operam o WhatsApp da organização com uma API key — listar, parear, enviar, consultar.',
      snippet: `claude mcp add --transport http talkium ${origin}/v1/mcp \\\n  --header "Authorization: Bearer tk_live_SUA_CHAVE_AQUI"`,
      to: '/developers/mcp',
      cta: 'Conectar um agente',
    },
    {
      key: 'webhooks',
      icon: <Webhook />,
      title: 'Webhooks',
      body: 'Receba mensagens e mudanças de status no seu sistema, com assinatura HMAC, reentrega com backoff e DLQ para reprocessar.',
      to: '/webhooks',
      cta: 'Cadastrar um webhook',
    },
    {
      key: 'automation',
      icon: <Workflow />,
      title: 'n8n, Make e Zapier',
      body: 'Para enviar: um nó HTTP Request com a API key no header Authorization. Para receber: um webhook da Talkium apontando para a URL do fluxo.',
      snippet: `POST ${origin}/v1/messages/text\nAuthorization: Bearer tk_live_SUA_CHAVE_AQUI\nContent-Type: application/json\n\n{ "instanceId": "ins_…", "to": "5511999990000", "text": "Olá!" }`,
      to: '/webhooks',
      cta: 'Ver webhooks',
    },
  ];

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="m-0 text-xl font-semibold">Integrações</h2>
          <p className="m-0 text-sm text-muted-foreground">
            Cinco caminhos para ligar a Talkium ao que você já usa. Todos com a mesma API key.
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {cards.map((card) => (
          <Card
            key={card.key}
            title={card.title}
            actions={card.tag ? <Badge tone="brand">{card.tag}</Badge> : undefined}
          >
            <div className="flex items-start gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary [&_svg]:size-4">
                {card.icon}
              </span>
              <p className="m-0 text-sm text-muted-foreground">{card.body}</p>
            </div>
            {card.snippet && (
              <div className="relative mt-3">
                <div className="absolute top-2 right-2">
                  <CopyButton value={card.snippet} label={`Copiar exemplo de ${card.title}`} />
                </div>
                <pre className="code text-[12px]" data-testid={`snippet-${card.key}`}>
                  {card.snippet}
                </pre>
              </div>
            )}
            <div className="mt-3">
              <Button variant="outline" size="sm" asChild>
                <Link to={card.to} className="no-underline">
                  {card.cta}
                </Link>
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}
